import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Text, 
  Grid, 
  GridItem, 
  Button, 
  Flex, 
  VStack, 
  HStack, 
  Badge, 
  Progress, 
  Tooltip, 
  useToast,
  Image
} from '@chakra-ui/react';
import { FaCoins, FaTrophy, FaMapMarkerAlt, FaLock, FaUnlock } from 'react-icons/fa';
import { GiTreasureMap, GiPirateFlag, GiIsland, GiPalmTree, GiShipWheel } from 'react-icons/gi';

const PalmHeistProgressMap = ({ userStats, onPlayGame, balance }) => {
  const toast = useToast();
  const [currentZone, setCurrentZone] = useState(1);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  
  // Initialize zones
  useEffect(() => {
    const totalZones = 10;
    const newZones = [];
    
    for (let i = 1; i <= totalZones; i++) {
      const zoneRtp = i <= 3 ? 55 : (i >= 10 ? 120 : 75 + (i - 1) * 5);
      const unlocked = i <= Math.max(1, Math.floor((userStats?.totalWagered || 0) / 100) + 1);
      
      newZones.push({
        id: i,
        name: `Zone ${i}`,
        description: getZoneDescription(i),
        rtp: zoneRtp,
        unlocked,
        rewards: {
          min: i * 10,
          max: i * 50
        },
        icon: getZoneIcon(i)
      });
    }
    
    setZones(newZones);
    
    // Set current zone based on user progress
    const calculatedZone = Math.min(
      totalZones, 
      Math.max(1, Math.floor((userStats?.totalWagered || 0) / 100) + 1)
    );
    setCurrentZone(calculatedZone);
    setSelectedZone(calculatedZone);
  }, [userStats]);
  
  // Get zone description based on zone number
  const getZoneDescription = (zoneNumber) => {
    const descriptions = [
      "Beginner's Bay - Start your journey here",
      "Shallow Waters - A bit more challenging",
      "Coral Reef - Hidden treasures await",
      "Deep Sea - Dangerous but rewarding",
      "Shipwreck Cove - Explore sunken ships",
      "Kraken's Lair - Face the mighty beast",
      "Mermaid Lagoon - Enchanting rewards",
      "Pirate's Hideout - Steal from the pirates",
      "Treasure Island - Almost at the big prize",
      "El Dorado - The legendary city of gold"
    ];
    
    return descriptions[zoneNumber - 1] || `Zone ${zoneNumber}`;
  };
  
  // Get zone icon based on zone number
  const getZoneIcon = (zoneNumber) => {
    const icons = [
      GiPalmTree,
      GiIsland,
      GiTreasureMap,
      GiShipWheel,
      GiPirateFlag,
      GiPirateFlag,
      GiIsland,
      GiPirateFlag,
      GiTreasureMap,
      GiPirateFlag
    ];
    
    return icons[zoneNumber - 1] || GiIsland;
  };
  
  // Handle zone selection
  const handleSelectZone = (zone) => {
    if (!zone.unlocked) {
      toast({
        title: "Zone Locked",
        description: `Play more to unlock Zone ${zone.id}. You need to wager at least ${zone.id * 100} SOL in total.`,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setSelectedZone(zone.id);
  };
  
  // Handle playing in the selected zone
  const handlePlay = () => {
    if (!selectedZone) return;
    
    const zone = zones.find(z => z.id === selectedZone);
    if (!zone) return;
    
    onPlayGame(10); // Fixed bet amount for this example
  };
  
  return (
    <Box 
      p={4} 
      borderRadius="md" 
      bg="rgba(0, 0, 0, 0.7)"
      border="1px solid rgba(255, 215, 0, 0.3)"
      boxShadow="0 0 10px rgba(255, 215, 0, 0.5)"
    >
      <VStack spacing={4} align="stretch">
        <Flex justify="space-between" align="center">
          <Text fontSize="xl" fontWeight="bold" color="gold">Palm Heist Progress Map</Text>
          <HStack>
            <FaCoins color="gold" />
            <Text color="white">{balance} JINU</Text>
          </HStack>
        </Flex>
        
        <Grid templateColumns="repeat(5, 1fr)" gap={2}>
          {zones.map((zone) => (
            <GridItem key={zone.id}>
              <Tooltip label={zone.description}>
                <Box
                  p={3}
                  borderRadius="md"
                  bg={selectedZone === zone.id ? "rgba(255, 215, 0, 0.2)" : "rgba(0, 0, 0, 0.5)"}
                  border="1px solid"
                  borderColor={selectedZone === zone.id ? "yellow.400" : "gray.700"}
                  cursor={zone.unlocked ? "pointer" : "not-allowed"}
                  opacity={zone.unlocked ? 1 : 0.6}
                  onClick={() => handleSelectZone(zone)}
                  transition="all 0.2s"
                  _hover={{
                    bg: zone.unlocked ? "rgba(255, 215, 0, 0.1)" : "rgba(0, 0, 0, 0.5)",
                  }}
                >
                  <VStack spacing={1}>
                    <Box fontSize="2xl" color={zone.unlocked ? "gold" : "gray.500"}>
                      {zone.unlocked ? (
                        <zone.icon />
                      ) : (
                        <FaLock />
                      )}
                    </Box>
                    <Text fontWeight="bold" color={zone.unlocked ? "white" : "gray.500"}>
                      Zone {zone.id}
                    </Text>
                    <Badge colorScheme={zone.unlocked ? "yellow" : "gray"}>
                      RTP: {zone.rtp}%
                    </Badge>
                  </VStack>
                </Box>
              </Tooltip>
            </GridItem>
          ))}
        </Grid>
        
        {selectedZone && (
          <Box 
            p={3} 
            borderRadius="md" 
            bg="rgba(0, 0, 0, 0.5)"
            border="1px solid rgba(255, 215, 0, 0.3)"
          >
            <VStack spacing={2} align="stretch">
              <Flex justify="space-between" align="center">
                <Text fontWeight="bold" color="white">
                  {zones.find(z => z.id === selectedZone)?.name}
                </Text>
                <Badge colorScheme="yellow">
                  RTP: {zones.find(z => z.id === selectedZone)?.rtp}%
                </Badge>
              </Flex>
              
              <Text color="gray.300" fontSize="sm">
                {zones.find(z => z.id === selectedZone)?.description}
              </Text>
              
              <Flex justify="space-between" align="center" mt={2}>
                <Text color="gray.300" fontSize="sm">
                  Potential Rewards: {zones.find(z => z.id === selectedZone)?.rewards.min} - {zones.find(z => z.id === selectedZone)?.rewards.max} JINU
                </Text>
                <Button 
                  colorScheme="yellow" 
                  size="sm"
                  onClick={handlePlay}
                >
                  Play (10 JINU)
                </Button>
              </Flex>
            </VStack>
          </Box>
        )}
        
        <Box>
          <Text color="gray.300" fontSize="sm" mb={1}>Your Progress</Text>
          <Progress 
            value={(userStats?.totalWagered || 0) % 100} 
            max={100}
            colorScheme="yellow"
            borderRadius="full"
            size="sm"
          />
          <Flex justify="space-between" mt={1}>
            <Text color="gray.400" fontSize="xs">
              Total Wagered: {userStats?.totalWagered || 0} JINU
            </Text>
            <Text color="gray.400" fontSize="xs">
              Next Zone: {Math.min(10, currentZone + 1)}
            </Text>
          </Flex>
        </Box>
      </VStack>
    </Box>
  );
};

export default PalmHeistProgressMap;
